import React from "react";
import {
  Box,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Paper,
  Typography,
  TablePagination,
  TextField,
  MenuItem,
  Grid,
  CircularProgress,
  Card,
  CardContent,
  Divider,
} from "@mui/material";

const CustomTable = ({
  columns = [],
  rows = [],
  count = 0,
  page = 0,
  rowsPerPage = 10,
  onPageChange,
  onRowsPerPageChange,
  searchBy = "",
  onSearchByChange,
  searchValue = "",
  onSearchValueChange,
  searchOptions = [],
  loading = false,
}) => {
  return (
    <Box sx={{ width: "100%" }}>
      {/* Top Bar */}
      <Grid container justifyContent="space-between" alignItems="center" mb={2} mt={3}>
        <Grid item>
          <Typography variant="subtitle2" fontWeight="bold">Total: {count}</Typography>
        </Grid>

        <Grid item>
          <Box display="flex" gap={1}>
            <TextField
              select
              size="small"
              value={searchBy}
              onChange={onSearchByChange}
            >
              {searchOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              size="small"
              placeholder="Search..."
              value={searchValue}
              onChange={onSearchValueChange}
            />
          </Box>
        </Grid>
      </Grid>

      {/* Table View (Only Desktop) */}
      <TableContainer
        component={Paper}
        sx={{
          borderRadius: 2,
          display: { xs: "none", md: "block" },
        }}
      >
        <Table>
          <TableHead sx={{ backgroundColor: "#1976d2" }}>
            <TableRow>
              {columns.map((col) => (
                <TableCell key={col.key} sx={{ fontWeight: "bold" ,  color: "white"  }}>
                  {col.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={columns.length} align="center">
                  <Box py={3}>
                    <CircularProgress />
                  </Box>
                </TableCell>
              </TableRow>
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={columns.length} align="center">
                  No records found
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row, idx) => (
                <TableRow
                  key={row.id || idx}
                  sx={{
                    backgroundColor: idx % 2 === 0 ? "#F9F9F9" : "#FFFFFF",
                    "&:hover": {
                      backgroundColor: "#D1ECF1",
                      cursor: "pointer",
                    },
                  }}
                >
                  {columns.map((col) => (
                    <TableCell key={col.key}>
                      {typeof col.render === "function"
                        ? col.render(row)
                        : row[col.key]}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Card View (Only Mobile) */}
      <Box sx={{ display: { xs: "block", md: "none" } }}>
        {loading ? (
          <Box textAlign="center" py={3}>
            <CircularProgress />
          </Box>
        ) : rows.length === 0 ? (
          <Typography align="center" py={2}>
            No records found
          </Typography>
        ) : (
          rows.map((row, idx) => (
            <Card
              key={row.id || idx}
              variant="outlined"
              sx={{
                mb: 2,
                backgroundColor: idx % 2 === 0 ? "#F9F9F9" : "#FFFFFF",
              }}
            >
              <CardContent>
                {columns.map((col, cidx) => (
                  <Box key={cidx} mb={1}>
                    <Typography variant="caption" color="text.secondary" fontWeight="bold">
                      {col.label}
                    </Typography>
                    <Typography variant="body2">
                      {typeof col.render === "function"
                        ? col.render(row)
                        : row[col.key]}
                    </Typography>
                    {cidx < columns.length - 1 && <Divider sx={{ my: 1 }} />}
                  </Box>
                ))}
              </CardContent>
            </Card>
          ))
        )}
      </Box>

      {/* Pagination */}
      <Box mt={2}>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={count}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={onPageChange}
          onRowsPerPageChange={onRowsPerPageChange}
        />
      </Box>
    </Box>
  );
};

export default CustomTable;
